import { useMutation } from '@tanstack/react-query'
import { IHttpError, IPaginateResponse, IResponse, Model, ObjectsFactory } from '../core'

export interface IAttendance {
  id: number
  created_at: string
  updated_at: string
  created_by: number
  updated_by: number
  name: string
  system_code: string
  company_code: string
  business_number: any
  address: any
  phone: any
  email: any
  bank_id: any
  bank_account_number: any
  establish_date: any
  company_type: any
  poc_firstname: any
  poc_lastname: any
  poc_phone: any
  total_user: number
  working_rate: string
  working_type: string
  setting_updated_at: string
  setting_updated_by: any
  logo_id: any
  status: string
  logo_file: any
  reconcile_1: IAttendanceReconcile1
  reconcile_2: IAttendanceReconcile2
  reconcile_3: IAttendanceReconcile3
  working_day: number
}

export interface IAttendanceReconcile1 {
  month: string
  status: string
}

export interface IAttendanceReconcile2 {
  month: string
  status: string
}

export interface IAttendanceReconcile3 {
  month: string
  status: any
}

export interface IAttendanceEmployee {
  user_id: string
  first_name: string
  employee_id: string
  employee_name: string
  status: string
  attendance: IAttendanceEmployeeAttendance[]
  gross_pay: any
  net_pay: any
  daily_wage: any
  currency_code: string
  avatar_file: any
  id: any
  is_allow_delete: boolean
}

export interface IAttendanceEmployeeAttendance {
  date: number
  status: string
}

export enum WorkingTypeEnum {
  Standard = 'standard',
  Extended = 'extended',
  FullWeek = 'full_week'
}

export enum AttendanceStatusEnum {
  Unreconciled = 'unreconciled',
  Reconciled = 'reconciled',
  Checking = 'checking'
}

export enum AttendanceEmployeeStatusEnum {
  WorkingDay = 'working_day',
  Absent = 'absent',
  NonWorkingDay = 'non_working_day'
}

export enum AttendanceStatusLogsEnum {
  Information = 'information',
  Warning = 'warning',
  Error = 'error',
  Success = 'success'
}

export const workingTypeConfigLabel = () => {
  const { __ } = useTranslator()

  return {
    [WorkingTypeEnum.Standard]: __('Standard (Mon-Fri)'),
    [WorkingTypeEnum.Extended]: __('Extended (Mon-Sat)'),
    [WorkingTypeEnum.FullWeek]: __('Full Week (Mon-Sun)')
  }
}

export const attendanceStatusConfigLabel = () => {
  const { __ } = useTranslator()

  return {
    [AttendanceStatusEnum.Unreconciled]: __('Unreconciled'),
    [AttendanceStatusEnum.Reconciled]: __('Reconciled'),
    [AttendanceStatusEnum.Checking]: __('Checking')
  }
}

const modelConfig = {
  path: '/attendance',
  model: 'ATTENDANCE'
}

export class Attendances extends Model {
  static queryKeys = {
    paginate: 'ATTENDANCE_PAGINATE_QUERY',
    findOne: 'ATTENDANCE_FIND_ONE_QUERY',
    getAttendance: 'ATTENDANCE_GET_ATTENDANCE_QUERY',
    updateAttendanceSettingByCompanyId: 'ATTENDANCE_UPDATE_ATTENDANCE_SETTING_BY_COMPANY_ID_QUERY',
    attendanceBulkSetting: 'ATTENDANCE_BULK_SETTING_QUERY',
    getAttendanceDetailByCompanyId: 'ATTENDANCE_GET_ATTENDANCE_DETAIL_BY_COMPANY_ID_QUERY',
    getAttendanceByCompanyIdEmployee: 'ATTENDANCE_GET_ATTENDANCE_BY_COMPANY_ID_EMPLOYEE_QUERY',
    attendanceReconcileByCompanyIdEmployee: 'ATTENDANCE_RECONCILE_BY_COMPANY_ID_EMPLOYEE_QUERY',
    checkUploadAttendanceReconcileByCompanyId: 'ATTENDANCE_CHECK_UPLOAD_RECONCILE_BY_COMPANY_ID_QUERY',
    getAttendanceDetailByCompanyIdEmployee: 'ATTENDANCE_GET_ATTENDANCE_DETAIL_BY_COMPANY_ID_EMPLOYEE_QUERY',
    getConpanyIdEmployeeHistory: 'ATTENDANCE_GET_CONPANY_ID_EMPLOYEE_HISTORY_QUERY',
    getAttendanceActivityLogs: 'ATTENDANCE_GET_ATTENDANCE_ACTIVITY_LOGS_QUERY'
  }

  static objects = ObjectsFactory.factory<IAttendance>(modelConfig, this.queryKeys)

  static getAttendance(params: any) {
    return {
      queryKey: [this.queryKeys.getAttendance, params],
      queryFn: () => {
        return this.api
          .get<IPaginateResponse<IAttendance>>({
            url: modelConfig.path,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static attendanceBulkSetting(data: any) {
    return {
      queryKey: [this.queryKeys.attendanceBulkSetting, data],
      queryFn: () => {
        return this.api.post<IResponse<any>>({
          url: `${modelConfig.path}/bulk-setting`,
          data
        })
      }
    }
  }

  static getAttendanceDetailByCompanyId(params: any) {
    return {
      queryKey: [this.queryKeys.getAttendanceDetailByCompanyId, params],
      queryFn: () => {
        return this.api
          .get<IResponse<any>>({
            url: `${modelConfig.path}/${params.companyId}`,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static getAttendanceByCompanyIdEmployee(params: any) {
    return {
      queryKey: [this.queryKeys.getAttendanceByCompanyIdEmployee, params],
      queryFn: () => {
        return this.api
          .get<IPaginateResponse<any>>({
            url: `${modelConfig.path}/${params.companyId}/employee`,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static checkUploadAttendanceReconcileByCompanyId(params: any) {
    return {
      queryKey: [this.queryKeys.checkUploadAttendanceReconcileByCompanyId, params],
      queryFn: () => {
        return this.api
          .get<IResponse<any>>({
            url: `${modelConfig.path}/${params.companyId}/reconcile/check`,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static getAttendanceDetailByCompanyIdEmployee(params: any) {
    return {
      queryKey: [this.queryKeys.getAttendanceDetailByCompanyIdEmployee, params],
      queryFn: () => {
        return this.api.get<IResponse<any>>({
          url: `${modelConfig.path}/${params.companyId}/employee/${params.userId}`,
          params
        })
      }
    }
  }

  static getConpanyIdEmployeeHistory(params: any) {
    return {
      queryKey: [this.queryKeys.getConpanyIdEmployeeHistory, params],
      queryFn: () => {
        return this.api
          .get<IResponse<any>>({
            url: `${modelConfig.path}/${params.companyId}/employee/${params.userId}/history`,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static exportAttendanceByCompanyIdEmployee(params: any, fileName: string = 'attendance_export.xlsx') {
    const { companyId, ...queryParams } = params

    return this.api.downloadFile(`${modelConfig.path}/${companyId}/employee`, fileName, {
      params: { ...queryParams, is_export: 1 }
    })
  }

  static exportAttendanceReconcileTemplate(params: any) {
    const { companyId, ...queryParams } = params

    return this.api.get<{ url: string }>({
      url: `${modelConfig.path}/${companyId}/reconcile-template`,
      params: { ...queryParams }
    })
  }

  static getAttendanceActivityLogs(params: any) {
    return {
      queryKey: [this.queryKeys.getAttendanceActivityLogs, params],
      queryFn: () => {
        return this.api
          .get<IPaginateResponse<any>>({
            url: `/activity`,
            params
          })
          .then(r => r.data)
      }
    }
  }
}

Attendances.setup(modelConfig)

// mutation
export function useUpdateAttendanceSettingByCompanyIdMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: any; data: any }>({
    mutationFn: ({ companyId, data }) => {
      return Attendances.api
        .put<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/setting`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useAttendanceBulkSettingMutation() {
  return useMutation<IResponse<any>, IHttpError, { data: any }>({
    mutationFn: data => {
      return Attendances.api
        .post<IResponse<any>>({
          url: `${modelConfig.path}/bulk-setting`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useAttendanceReconcileByCompanyIdEmployeeMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: number; data: any }>({
    mutationFn: ({ companyId, data }) => {
      return Attendances.api
        .post<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/employee`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useAttendanceByCompanyEmployeeBulkMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: number; data: any }>({
    mutationFn: ({ companyId, data }) => {
      return Attendances.api
        .post<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/employee/bulk`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useAttendanceConfirmReconcileByCompanyIdEmployeeMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: number; data: any }>({
    mutationFn: ({ companyId, data }) => {
      return Attendances.api
        .post<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/confirm-reconcile`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useUploadAttendanceReconcileByCompanyIdMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: number; data: FormData }>({
    mutationFn: ({ companyId, data }) => {
      return Attendances.api
        .post<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/reconcile`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useUpdateAttendancePayrollByCompanyIdEmployeeMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: number; userId: number; data: any }>({
    mutationFn: ({ companyId, userId, data }) => {
      return Attendances.api
        .put<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/employee/${userId}/payroll`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useAttendanceBulkSettingByCompanyIdMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: number; data: any }>({
    mutationFn: ({ companyId, data }) => {
      return Attendances.api
        .put<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/setting`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useAttendanceStatusUpdateEmployeeMutation() {
  return useMutation<IResponse<any>, IHttpError, { companyId: number; userId: number; data: any }>({
    mutationFn: ({ companyId, userId, data }) => {
      return Attendances.api
        .put<IResponse<any>>({
          url: `${modelConfig.path}/${companyId}/employee/${userId}`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useAttendanceDeleteEmployeeMutation() {
  return useMutation<IResponse<any>, IHttpError, { id: number | string }>({
    mutationFn: ({ id }) => {
      return Attendances.api
        .delete<IResponse<any>>({
          url: `${modelConfig.path}/${id}`
        })
        .then(r => r.data)
    }
  })
}
